import { isDefined } from '@sama/js-core';
import { compact, every, isEqual, times } from '@sama/micro-dash';
import {
  addPoints,
  dotProduct,
  scalePoint,
} from '../../../../shared/misc-utilities/point-utils';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';

type Vector = [number, number, number] | undefined;

/**
 * these are in the form:
 * ```
 * adjacencies[v] = [
 *   vertex above/below v,
 *   vertex along the line from v to vanishing point 0,
 *   vertex along the line from v to vanishing point 1,
 * ]
 * ```
 */
const vertexAdjacencies = [
  [1, 2, 4], // vertex 0
  [0, 3, 5], // vertex 1
  [3, 0, 6], // vertex 2
  [2, 1, 7], // vertex 3
  [5, 6, 0], // vertex 4
  [4, 7, 1], // vertex 5
  [7, 4, 2], // vertex 6
  [6, 5, 3], // vertex 7
];

/**
 * these are in the form:
 * ```
 * vanishingPointAdjacencies[i] = [
 *   [two vertices that "point" at the vanishing point], ...
 * ]
 * ```
 */
const vanishingPointAdjacencies = [
  [
    [0, 2],
    [1, 3],
    [4, 6],
    [5, 7],
  ],
  [
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ],
];

// I don't know if this naming is correct, but this code treats the cross
// product of 2 points as representing the line between them, and the cross
// product of 2 "lines" as representing the point where they intersect.
// Normalizing one of those "points" then gives the correct x, y coordinates to
// display.

// This is uses "projective geometry". The best summary I found on it:
// https://plus.maths.org/content/real-projective-plane-homogeneous-coordinates

class CuboidSolver {
  vanishingLine?: Vector;
  vanishingPoints = new Array<Vector>(2).fill(undefined);
  vertices = new Array<Vector>(8).fill(undefined);

  private changed = false;

  /**
   * This will solve for as many of the vertices as possible. If you've already
   * set enough of the instance variables to lock in the full cuboid, this will
   * find that solution. Exception: if any of the vertices are on the vanishing
   * line extra things may need to be specified.
   */
  solve() {
    this.vanishingPoints = this.vanishingPoints.map(
      (p, i) => p || this.solveVanishingPoint(i),
    );
    this.vanishingLine =
      this.vanishingLine ||
      crossProduct(this.vanishingPoints[0], this.vanishingPoints[1]);
    do {
      this.changed = false;
      this.vertices = this.vertices.map((v, i) => v || this.solveVertex(i));
    } while ( // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      this.changed
    );
    return this.getVerticesInExternalFormat();
  }

  private solveVanishingPoint(id: number) {
    const vertexOnLine = vanishingPointAdjacencies[id]
      .map((adj) => this.vertices[adj[1]])
      .find((v) => isPointOnLine(v, this.vanishingLine));
    if (vertexOnLine) {
      return vertexOnLine;
    }

    const lines = compact([
      this.vanishingLine,
      ...vanishingPointAdjacencies[id].map((vertexIds) =>
        crossProduct(this.vertices[vertexIds[0]], this.vertices[vertexIds[1]]),
      ),
    ]);
    return intersectionOfLines(lines[0], lines[1]);
  }

  private solveVertex(v: number) {
    const [vert, a, b] = vertexAdjacencies[v];
    const verticalLine = this.verticalLineThrough(vert);
    const lineThroughA = this.lineThroughVanishingPointAndVertex(0, a);
    const lineThroughB = this.lineThroughVanishingPointAndVertex(1, b);
    return (
      this.newVertexFromIntersection(verticalLine, lineThroughA) ||
      this.newVertexFromIntersection(verticalLine, lineThroughB) ||
      this.newVertexFromIntersection(lineThroughA, lineThroughB)
    );
  }

  private getVerticesInExternalFormat() {
    return times(8, (i) => {
      const vertex = this.vertices[i];
      return vertex && (vertex.slice(0, 2) as Point2D);
    });
  }

  private newVertexFromIntersection(line1: Vector, line2: Vector) {
    const intersection = crossProduct(line1, line2);
    if (!isFinite(intersection)) {
      return undefined;
    }

    this.changed = true;
    return homogenize(intersection!);
  }

  private lineThroughVanishingPointAndVertex(
    vanishingPointId: number,
    vertexId: number,
  ) {
    const vertex = this.vertices[vertexId];
    const vanishingPoint = this.vanishingPoints[vanishingPointId];
    if (vanishingPoint) {
      return crossProduct(vanishingPoint, vertex);
    } else {
      // No vanishing point means that face is perfectly facing the camera.
      return parallelLineThroughPoint(vertex, this.vanishingLine);
    }
  }

  private verticalLineThrough(vertexId: number) {
    return perpendicularLineThroughPoint(
      this.vertices[vertexId],
      this.vanishingLine,
    );
  }
}

export function calculateCuboidVertices(keyPoints: Point2D[]) {
  const solver = new CuboidSolver();

  const [pv, v2, v0, v4, v1] = keyPoints.map((p) => ([...p, 1] as Vector)!);
  const vanishingLine = perpendicularLineThroughPoint(pv, crossProduct(v0, v1));
  solver.vanishingLine = vanishingLine;
  solver.vertices[0] = v0;
  solver.vertices[1] = v1;
  solver.vertices[2] = v2;
  solver.vertices[4] = v4;

  if (isPointOnLine(v2, vanishingLine) && isPointOnLine(v4, vanishingLine)) {
    // The x coordinate for 6 & 7 could conceivably fall in a range. Choose the
    // midpoint between 2 & 4 to make it smooth with other calculations.
    solver.vertices[6] = scalePoint(addPoints(v2, v4), 0.5);
  }

  return solver.solve();
}

/**
 * Determines whether the given array of key points (which may be a partial or
 * complete cuboid) are valid (i.e. able to be solved by the solver).
 */
export function isValidPartialCuboid(keyPoints: Point2D[]) {
  const allKeyPoints = fillInMissingPointsWithDummyData(keyPoints);

  // Special case to treat certain "flat" cuboids as invalid
  if (
    isEqual(allKeyPoints[1], allKeyPoints[2]) ||
    isEqual(allKeyPoints[2], allKeyPoints[3])
  ) {
    return false;
  }

  return every(calculateCuboidVertices(allKeyPoints), isDefined);
}

function intersectionOfLines(l1: Vector, l2: Vector) {
  const p = crossProduct(l1, l2);
  return isFinite(p) ? p : undefined;
}

// https://math.stackexchange.com/a/3301054
function parallelLineThroughPoint(p: Vector, l: Vector) {
  const pointAtInfinity: Vector = l && [l[1], -l[0], 0];
  return crossProduct(p, pointAtInfinity);
}

// https://math.stackexchange.com/a/3301054
function perpendicularLineThroughPoint(p: Vector, l: Vector) {
  const pointAtInfinity: Vector = l && [l[0], l[1], 0];
  return crossProduct(p, pointAtInfinity);
}

function isPointOnLine(p: Vector, l: Vector) {
  return !!(p && l && Math.abs(dotProduct(p, l)) < 1e-5);
}

function isFinite(p: Vector) {
  return p && Math.abs(p[2]) > 1e-5;
}

function crossProduct(j: Vector, k: Vector): Vector {
  if (j && k) {
    const x = j[1] * k[2] - j[2] * k[1];
    const y = j[2] * k[0] - j[0] * k[2];
    const z = j[0] * k[1] - j[1] * k[0];
    return [x, y, z];
  } else {
    return undefined;
  }
}

/**
 * We are using "homogeneous coordinates" from "projective geometry" (those are
 * some search terms for you).
 */
function homogenize(p: NonNullable<Vector>) {
  return p.map((point) => point / p[2]) as Vector;
}

/**
 * Given an array of up to 5 points, fills in any missing ones at the end with
 * valid dummy points. This allows us to run the solver and determine cuboid
 * validity even if not all points have been created yet.
 */
// eslint-disable-next-line complexity -- if you need to modify this code, fix this first
function fillInMissingPointsWithDummyData([p1, p2, p3, p4, p5]: Point2D[]) {
  // p2 is 1 pixel down from p1
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-param-reassign -- fix me!
  p2 = p2 || addPoints(p1, [0, 1]);

  // p3 is down and right of p2 (or up and right if above vanishing line)
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-param-reassign -- fix me!
  p3 = p3 || addPoints(p2, [1, p2[0] - p1[0] || 1]);

  // p4 is same horizontally as p2, in the same direction as p3 (nudged one
  // pixel if p2 and p3 were on the same horizontal line)
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-param-reassign -- fix me!
  p4 = p4 || [2 * p3[0] - p2[0], p2[1] === p3[1] ? p2[1] + 1 : p2[1]];

  // p5 is same horizontally as p3, halfway between p3 and the vanishing line
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-param-reassign -- fix me!
  p5 = p5 || [p3[0], (p1[0] + p3[1]) / 2];

  return [p1, p2, p3, p4, p5];
}
